import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Info, Percent } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PlatformCommissionBanner — shown on Vendor + Professional dashboards so
 * everyone understands the markup the platform adds on top of their listing
 * prices when customers see them on Instant Pricing.
 *
 * Reads from the public `/api/referral/settings` endpoint and highlights the
 * lane that matches the current user (vendor / venue vendor / professional).
 *
 * Props:
 *   - role: 'vendor' | 'professional'
 *   - category: vendor's category (e.g. 'venue', 'decorator', ...)
 */
const LANE_LABEL = {
  vendor: 'Vendor Listings',
  venue: 'Venue Listings',
  professional: 'Professional Listings',
};

const LANE_DESC = {
  vendor: 'Catering, Decoration, Photography, DJ, Flowers, Equipment, etc.',
  venue: 'Mandapam, Hotel, Villa, Beach Villa, Convention Center, Banquet, etc.',
  professional: 'Priest, Chef, Makeup, Mehendi, Choreographer, Waiter, etc.',
};

const PlatformCommissionBanner = ({ role, category }) => {
  const [pc, setPc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/referral/settings`);
        if (!cancelled) setPc(data?.platform_commission || null);
      } catch {
        if (!cancelled) setPc(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (!pc) return null;

  // Which lane belongs to *this* user.
  const myKey = role === 'professional'
    ? 'professional'
    : (String(category || '').toLowerCase() === 'venue' ? 'venue' : 'vendor');

  const lanes = [
    { key: 'vendor', pct: pc.vendor },
    { key: 'venue', pct: pc.venue },
    { key: 'professional', pct: pc.professional },
  ].filter((l) => typeof l.pct === 'number');

  return (
    <div
      className="bg-amber-50 border border-amber-300 rounded p-4"
      data-testid="platform-commission-banner"
    >
      <div className="flex items-start gap-3">
        <div className="bg-amber-200 p-2 rounded-full flex-shrink-0">
          <Percent size={18} weight="bold" className="text-amber-900" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-base font-semibold text-amber-900 mb-1">
            Platform Commission
          </h3>
          <p className="text-xs text-amber-900/80 mb-3">
            This markup is added on top of your listing prices when customers see them on Instant Pricing. <span className="font-semibold">You always quote and receive your listing price</span> — the difference is the platform's commission.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {lanes.map((l) => {
              const isMine = l.key === myKey;
              return (
                <div
                  key={l.key}
                  className={`px-3 py-2 rounded border ${isMine ? 'border-amber-600 bg-amber-100 ring-1 ring-amber-400' : 'border-amber-200 bg-white'}`}
                  data-testid={`commission-lane-${l.key}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-[10px] tracking-[0.15em] uppercase font-bold ${isMine ? 'text-amber-900' : 'text-text-secondary'}`}>
                      {LANE_LABEL[l.key]}
                    </span>
                    {isMine && (
                      <span className="text-[8px] uppercase tracking-wider font-bold bg-amber-700 text-white px-1.5 py-0.5 rounded" data-testid={`commission-lane-${l.key}-mine`}>
                        You
                      </span>
                    )}
                  </div>
                  <div className={`font-heading text-2xl font-bold mt-0.5 ${isMine ? 'text-amber-900' : 'text-text-primary'}`} data-testid={`commission-lane-${l.key}-pct`}>
                    {l.pct}%
                  </div>
                  <div className="text-[10px] text-text-secondary leading-tight">{LANE_DESC[l.key]}</div>
                </div>
              );
            })}
          </div>
          <div className="flex items-start gap-1.5 mt-3 text-[11px] text-amber-900/75">
            <Info size={12} className="mt-0.5 flex-shrink-0" />
            <span>
              Example: if you list a service at <span className="font-mono">₹10,000</span> and your commission is <span className="font-mono font-semibold">{pc[myKey]}%</span>, the customer sees <span className="font-mono font-semibold">₹{(10000 * (1 + (pc[myKey] || 0) / 100)).toLocaleString('en-IN')}</span>. You still receive your ₹10,000.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformCommissionBanner;
